import { useTranslation } from 'react-i18next';
import { useStores } from '../../../stores/store';
import { useEffect, useState } from 'react';
import Moment from 'moment';
import React from 'react';
import { Table } from '../../common/table';
import Popup from 'reactjs-popup';
import { config } from '../../../config';

function SchoolDevicesPage(props) {
	const { auth } = useStores();
  const { t, i18n } = useTranslation();

	const [error, setError] = useState<string>("");
  const [showError, setShowError] = useState<boolean>(false);
  const [isDataError, setIsDataError] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [shownCreatePopup, setShownCreatePopup] = useState(false);
  const [shownAssociatePopup, setShownAssociatePopup] = useState(false);
  const [shownEditPopup, setShownEditPopup] = useState(false);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // Form fields for creating new device
  const [newDeviceName, setNewDeviceName] = useState("");
  const [newDeviceCode, setNewDeviceCode] = useState("");

  // Form field for associating license
  const [associateCode, setAssociateCode] = useState("");

  // Form field for editing device name
  const [editDeviceName, setEditDeviceName] = useState("");

  // License details
  const [selectedDeviceLicenses, setSelectedDeviceLicenses] = useState([]);
  const [showLicensesPopup, setShowLicensesPopup] = useState(false);
  const [isLicensesLoading, setIsLicensesLoading] = useState(false);

  useEffect(() => {
    refreshData();
  }, [])

  const refreshData = async () => {
    setIsDataLoading(true);
    setIsDataError(false);

    try {
      const response = await auth.api.get(`${config.getEndpointUri()}/registry/devices/`);
      setDevices(response.data || []);
      setIsDataLoading(false);
    } catch (error) {
      console.error('Error fetching devices:', error);
      setIsDataLoading(false);
      setIsDataError(true);
    }
  }

  const handleCreateDevice = async (e) => {
    e.preventDefault();

    try {
      await auth.api.post(`${config.getEndpointUri()}/registry/devices/`, {
        device_custom_name: newDeviceName,
        activation_code: newDeviceCode
      });

      setShownCreatePopup(false);
      setNewDeviceName("");
      setNewDeviceCode("");
      refreshData();
    } catch (error) {
      console.error('Error creating device:', error);
      setShowError(true);
      setError(error.response?.data?.msg || t('errorCreatingDevice'));
    }
  }

  const handleAssociateLicense = async (e) => {
    e.preventDefault();

    if (!selectedDevice) return;

    try {
      await auth.api.post(`${config.getEndpointUri()}/registry/devices/${selectedDevice.id}/associate_code/`, {
        activation_code: associateCode
      });

      setShownAssociatePopup(false);
      setAssociateCode("");
      setSelectedDevice(null);
      refreshData();
    } catch (error) {
      console.error('Error associating license:', error);
      setShowError(true);
      setError(error.response?.data?.msg || t('errorAssociatingLicense'));
    }
  }

  const openAssociatePopup = (device) => {
    setSelectedDevice(device);
    setAssociateCode("");
    setShownAssociatePopup(true);
  }

  const openEditPopup = (device) => {
    setSelectedDevice(device);
    setEditDeviceName(device.device_custom_name || "");
    setShownEditPopup(true);
  }

  const handleEditDevice = async (e) => {
    e.preventDefault();

    if (!selectedDevice) return;

    try {
      await auth.api.patch(`${config.getEndpointUri()}/registry/devices/${selectedDevice.id}/`, {
        device_custom_name: editDeviceName
      });

      setShownEditPopup(false);
      setEditDeviceName("");
      setSelectedDevice(null);
      refreshData();
    } catch (error) {
      console.error('Error editing device:', error);
      setShowError(true);
      setError(error.response?.data?.msg || t('errorEditingDevice'));
    }
  }

  const fetchDeviceLicenses = async (device) => {
    setIsLicensesLoading(true);
    setSelectedDevice(device);
    setShowLicensesPopup(true);

    try {
      console.log('Fetching licenses for device:', device.id);
      const response = await auth.api.get(`${config.getEndpointUri()}/registry/activation-codes/?device=${device.id}`);
      console.log('Licenses response:', response.data);
      setSelectedDeviceLicenses(response.data?.results || response.data || []);
      setIsLicensesLoading(false);
    } catch (error) {
      console.error('Error fetching licenses:', error);
      console.error('Error details:', error.response?.data);
      setIsLicensesLoading(false);
      setShowError(true);
      setError(error.response?.data?.msg || error.response?.data?.detail || t('errorLoadingLicenses'));
    }
  }

  const parseLicenseJson = (jsonString) => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  }

  const columns = React.useMemo(
    () => [
      {
          Header: t('devices'),
          columns: [
            {
                Header: '',
                accessor: 'edit',
                Cell: ({ cell }) => (
                  <button
                    onClick={() => openEditPopup(cell.row.original)}
                    className="text-gray-600 hover:text-gray-900 cursor-pointer"
                    title={t('edit')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                )
            },
            {
                Header: t('deviceName'),
                accessor: 'device_custom_name',
                Cell: ({ cell }) => (
                  <button
                    onClick={() => fetchDeviceLicenses(cell.row.original)}
                    className="text-blue-600 hover:underline cursor-pointer"
                  >
                    {cell.row.values.device_custom_name || t('noName')}
                  </button>
                )
            },
            {
              Header: t('deviceIdentifier'),
              accessor: "device_identifier",
            },
            {
              Header: t('licensesCount'),
              accessor: "activation_codes_count",
              Cell: ({ cell }) => {
                return (
                  <button
                    onClick={() => fetchDeviceLicenses(cell.row.original)}
                    className="text-blue-600 underline cursor-pointer hover:text-blue-800"
                    title={t('licensesForDevice')}
                  >
                    {cell.row.values.activation_codes_count || 0}
                  </button>
                );
              }
            },
            {
              Header: t('registrationDate'),
              accessor: "registration_date",
              Cell: ({ cell }) => {
                if (!cell.row.original.has_device_data) {
                  return <span className="text-gray-400 italic">{t('deviceNotActivated')}</span>;
                }
                return cell.row.values.registration_date ? Moment(cell.row.values.registration_date).format('DD/MM/yyyy HH:mm') : '-';
              }
            },
            {
              Header: t('actions'),
              accessor: "id",
              Cell: ({ cell }) => {
                if (!cell.row.original.has_device_data) {
                  return <span className="text-gray-400 italic">{t('deviceNotActivated')}</span>;
                }
                return (
                  <button
                    onClick={() => openAssociatePopup(cell.row.original)}
                    className="bg-primary hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  >
                    {t('associateLicense')}
                  </button>
                );
              }
            },
          ],
      },
    ], [t]
  );

	return (
		<div className="grid place-items-center">
      {/* Create Device Popup */}
      <Popup
        open={shownCreatePopup}
        position="right center"
        closeOnDocumentClick
        onClose={() => setShownCreatePopup(false)}
      >
        <form onSubmit={handleCreateDevice} className="flex flex-col items-center gap-6 p-6 bg-white rounded-lg shadow-lg">
          <h2 className="text-xl font-bold">{t('createNewDevice')}</h2>

          <div className="w-full">
            <label className="block text-sm font-medium mb-2">{t('deviceName')} ({t('optional')})</label>
            <input
              value={newDeviceName}
              onChange={(e) => setNewDeviceName(e.target.value)}
              className="shadow appearance-none border rounded w-full p-2 focus:outline-none focus:shadow-outline"
              placeholder={t('deviceNamePlaceholder')}
            />
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium mb-2">{t('activationCode')} *</label>
            <input
              value={newDeviceCode}
              onChange={(e) => setNewDeviceCode(e.target.value)}
              className="shadow appearance-none border rounded w-full p-2 focus:outline-none focus:shadow-outline"
              placeholder={t('activationCodePlaceholder')}
              required
            />
          </div>

          <div className="w-full flex flex-row justify-center gap-6">
            <button className="bg-primary text-white font-bold py-2 px-4 rounded-full" type="submit">{t('create')}</button>
            <button
              onClick={(e) => { e.preventDefault(); setShownCreatePopup(false) }}
              className="border-2 border-primary text-primary font-bold py-2 px-4 rounded-full"
            >
              {t('cancel')}
            </button>
          </div>
        </form>
      </Popup>

      {/* Edit Device Popup */}
      <Popup
        open={shownEditPopup}
        position="right center"
        closeOnDocumentClick
        onClose={() => setShownEditPopup(false)}
      >
        <form onSubmit={handleEditDevice} className="flex flex-col items-center gap-6 p-6 bg-white rounded-lg shadow-lg">
          <h2 className="text-xl font-bold">{t('editDeviceName')}</h2>

          {selectedDevice && (
            <div className="w-full text-center">
              <p><strong>{t('device')}:</strong> {selectedDevice.device_identifier}</p>
            </div>
          )}

          <div className="w-full">
            <label className="block text-sm font-medium mb-2">{t('deviceName')}</label>
            <input
              value={editDeviceName}
              onChange={(e) => setEditDeviceName(e.target.value)}
              className="shadow appearance-none border rounded w-full p-2 focus:outline-none focus:shadow-outline"
              placeholder={t('deviceNamePlaceholder')}
            />
          </div>

          <div className="w-full flex flex-row justify-center gap-6">
            <button className="bg-primary text-white font-bold py-2 px-4 rounded-full" type="submit">{t('save')}</button>
            <button
              onClick={(e) => { e.preventDefault(); setShownEditPopup(false) }}
              className="border-2 border-primary text-primary font-bold py-2 px-4 rounded-full"
            >
              {t('cancel')}
            </button>
          </div>
        </form>
      </Popup>

      {/* Associate License Popup */}
      <Popup
        open={shownAssociatePopup}
        position="right center"
        closeOnDocumentClick
        onClose={() => setShownAssociatePopup(false)}
      >
        <form onSubmit={handleAssociateLicense} className="flex flex-col items-center gap-6 p-6 bg-white rounded-lg shadow-lg">
          <h2 className="text-xl font-bold">{t('associateLicenseToDevice')}</h2>

          {selectedDevice && (
            <div className="w-full text-center">
              <p><strong>{t('device')}:</strong> {selectedDevice.device_custom_name || selectedDevice.device_identifier}</p>
            </div>
          )}

          <div className="w-full">
            <label className="block text-sm font-medium mb-2">{t('activationCode')} *</label>
            <input
              value={associateCode}
              onChange={(e) => setAssociateCode(e.target.value)}
              className="shadow appearance-none border rounded w-full p-2 focus:outline-none focus:shadow-outline"
              placeholder={t('activationCodePlaceholder')}
              required
            />
          </div>

          <div className="w-full flex flex-row justify-center gap-6">
            <button className="bg-primary text-white font-bold py-2 px-4 rounded-full" type="submit">{t('associate')}</button>
            <button
              onClick={(e) => { e.preventDefault(); setShownAssociatePopup(false) }}
              className="border-2 border-primary text-primary font-bold py-2 px-4 rounded-full"
            >
              {t('cancel')}
            </button>
          </div>
        </form>
      </Popup>

      {/* Licenses Details Popup */}
      <Popup
        open={showLicensesPopup}
        position="right center"
        closeOnDocumentClick
        onClose={() => setShowLicensesPopup(false)}
      >
        <div className="flex flex-col items-center gap-6 p-6 bg-white rounded-lg shadow-lg max-h-[80vh] overflow-y-auto" style={{ minWidth: '600px' }}>
          <h2 className="text-xl font-bold">{t('licensesForDevice')}</h2>

          {selectedDevice && (
            <div className="w-full text-center">
              <p><strong>{t('device')}:</strong> {selectedDevice.device_custom_name || selectedDevice.device_identifier}</p>
            </div>
          )}

          {isLicensesLoading && <p>{t('loading')}...</p>}

          {!isLicensesLoading && selectedDeviceLicenses.length === 0 && (
            <p>{t('noLicensesAssociated')}</p>
          )}

          {!isLicensesLoading && selectedDeviceLicenses.length > 0 && (
            <div className="w-full">
              {selectedDeviceLicenses.map((license, index) => {
                const licenseData = parseLicenseJson(license.license_data);
                return (
                  <div key={license.id} className="border rounded p-4 mb-4 bg-gray-50">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <strong>{t('code')}:</strong> {license.code}
                      </div>
                      <div>
                        <strong>{t('type')}:</strong> {license.type === 1 ? 'Pinguin' : license.type === 2 ? 'Miri' : license.type === 3 ? 'Miri Pro' : t('unknown')}
                      </div>
                      <div>
                        <strong>{t('activationDate')}:</strong> {license.activation_date ? Moment(license.activation_date).format('DD/MM/yyyy') : '-'}
                      </div>
                      <div>
                        <strong>{t('validityYear')}:</strong> {license.validity_year === 1000 ? '-' : (license.validity_year || '-')}
                      </div>
                      {licenseData && (
                        <>
                          {licenseData.school_name && (
                            <div className="col-span-2">
                              <strong>{t('schoolName')}:</strong> {licenseData.school_name}
                            </div>
                          )}
                          {licenseData.num_licenses && (
                            <div>
                              <strong>{t('numLicenses')}:</strong> {licenseData.num_licenses}
                            </div>
                          )}
                          {licenseData.expiration_date && (
                            <div>
                              <strong>{t('expirationDate')}:</strong> {Moment(licenseData.expiration_date).format('DD/MM/yyyy')}
                            </div>
                          )}
                          <div className="col-span-2">
                            <strong>{t('details')}:</strong>{' '}
                            {[
                              licenseData.countries?.map(c => c?.name).filter(Boolean).join(', '),
                              licenseData.educationLevels?.map(e => e?.description || e?.name).filter(Boolean).join(', '),
                              licenseData.lessonGroups?.map(g => g?.name).filter(Boolean).join(', '),
                            ].filter(Boolean).join(' - ') || '-'}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="w-full flex flex-row justify-center gap-6">
            <button
              onClick={() => setShowLicensesPopup(false)}
              className="bg-primary text-white font-bold py-2 px-4 rounded-full"
            >
              {t('close')}
            </button>
          </div>
        </div>
      </Popup>

      {/* Error Popup */}
      <Popup
        open={showError}
        position="right center"
        closeOnDocumentClick
        onClose={() => setShowError(false)}
      >
        <div className="w-full flex flex-col items-center gap-6 m-4 p-4 bg-white rounded-lg shadow-lg">
          <p className="text-red-600">{error}</p>

          <div className="w-full flex flex-row justify-center gap-6">
            <button
              onClick={() => setShowError(false)}
              className="bg-primary text-white font-bold py-2 px-4 rounded-full"
            >
              {t('ok')}
            </button>
          </div>
        </div>
      </Popup>

      <div className="w-full flex flex-row justify-end">
        <button
          onClick={() => { setNewDeviceName(""); setNewDeviceCode(""); setShownCreatePopup(true) }}
          className="bg-primary hover:bg-blue-700 text-white font-bold p-4 rounded-full mr-4"
        >
          {t('createNewDevice')}
        </button>
      </div>

      <div className="w-full flex flex-col items-center justify-center">
        {isDataLoading && <p>{t('loading')}...</p>}
        {isDataError && <p className="text-red-600">{t('errorLoadingData')}</p>}
        {!isDataError && !isDataLoading && devices.length > 0 && <Table columns={columns} data={devices} /> }
        {!isDataError && !isDataLoading && devices.length === 0 && <p>{t('noDevices')}</p>}
			</div>
		</div>
	)
}

export default SchoolDevicesPage;
